import { Select, MenuItem, FormControl, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { Box, Text, Accordion, Button } from "@/ui";
import { useState } from "react";
import Calendar from "react-calendar";
import { useNavigate, useLocation } from "react-router-dom";
import { addMonths } from "date-fns";
import qs from "query-string";
import { Report, ReportDossier } from "@/types/nurse";
import { formatUTC } from "@/utils/format";
import { useQueryParams } from "@/utils/useQueryParams";
import { colorPalette } from "@/theme/ThemeProvider";
import { FactureGroupMode, groupFactures, getDossierStatusLabel } from "@/utils/report";
import { FactureTable } from "../FactureTable";
import { PraticienSelector } from "@/components/common/PraticienSelector";

// Composant pour afficher un dossier de facturation (mobile)
function DossierSectionMobile({ dossier, groupMode }: { dossier: ReportDossier; groupMode: FactureGroupMode }) {
  const theme = useTheme();
  const dossierDate = new Date(dossier.date);
  const statusLabel = getDossierStatusLabel(dossier.status);
  const statusColor = dossier.status === 1 ? colorPalette.green.dark : dossier.status === 2 ? colorPalette.red.main : undefined;
  const groups = groupFactures(dossier.factures, groupMode);

  const DossierHeaderMobile = () => (
    <Box flexDirection="column" width="100%" alignItems="flex-start" style={{ paddingRight: 8 }}>
      <Box flexDirection="row" alignItems="center" flexWrap="wrap">
        <Text fontSize={12} fontWeight="600">
          Dossier du {formatUTC(dossierDate, "dd/MM/yyyy")} - {dossier.nb_facture} facture(s)
        </Text>
        {statusLabel && (
          <Text
            fontSize={10}
            fontWeight="700"
            color={statusColor}
            style={{ border: `1px solid ${statusColor}`, borderRadius: 20, padding: "1px 8px", marginLeft: 8 }}
          >
            {statusLabel}
          </Text>
        )}
      </Box>
      {/*
      <Text fontSize={10} color="#666">
        {dossier.gestionnaire} - {dossier.agence}
      </Text>
      */}
      {!!dossier.contenu && (
        <Text fontSize={10} color="#666" style={{ fontStyle: "italic" }}>
          Commentaire: {dossier.contenu}
        </Text>
      )}
      {dossier.status === 2 && !!dossier.libelle_non_facture && (
        <Text fontSize={10} color={colorPalette.red.main} style={{ fontStyle: "italic" }}>
          Motif: {dossier.libelle_non_facture}
        </Text>
      )}
      <Text fontSize={14} fontWeight="700" color="#FF008D">
        {parseFloat(dossier.montant).toFixed(2)} €
      </Text>
    </Box>
  );

  return (
    <Box width="100%" py={1} flexDirection="column" alignItems={"center"}>
      <Accordion width="99%" title={`dossier-${dossier.dossiers_facturation_id}`} HeaderComponent={DossierHeaderMobile} boxShadow="none">
        {groupMode === "none" ? (
          <Box width="100%" flexDirection="column" alignItems="flex-start" mb={2} mt={2}>
            <FactureTable factures={dossier.factures} />
          </Box>
        ) : (
          groups.map((group, groupIndex) => {
            const sum = group.factures.reduce((acc, f) => acc + parseFloat(f.montant || "0"), 0);
            const GroupHeaderMobile = () => (
              <Box flexDirection="row" justifyContent="space-between" alignItems="center" width="100%">
                <Text fontSize={11} fontWeight="600">
                  {group.label}
                </Text>
                <Text fontSize={12} fontWeight="700" color={theme.palette.primary.main}>
                  {sum.toFixed(2)} €
                </Text>
              </Box>
            );
            return (
              <Box key={`${group.key}-${groupIndex}`} width="100%" flexDirection="column" alignItems="center" mb={2} mt={2}>
                <Accordion
                  width="90%"
                  title={`group-${group.key}-${groupIndex}`}
                  HeaderComponent={GroupHeaderMobile}
                  boxShadow={`0 0 0 1px ${theme.palette.grey[300]}`}
                >
                  <FactureTable factures={group.factures} />
                </Accordion>
              </Box>
            );
          })
        )}
      </Accordion>
    </Box>
  );
}

export function ReportViewMobile({ reports, periodLabel }: any) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();
  const [groupMode, setGroupMode] = useState<FactureGroupMode>("none");
  const [periodVisible, setPeriodVisible] = useState(false);
  const report: Report | undefined = reports?.[0];
  const hasDossiers = !!report?.dossiers?.length;
  const totalMontant = report?.dossiers?.reduce((acc, d) => acc + parseFloat(d.montant || "0"), 0) || 0;

  const currentPeriod = params.period ? new Date(Number(params.period)) : new Date();
  const now = new Date();
  const isCurrentMonth = currentPeriod.getFullYear() === now.getFullYear() && currentPeriod.getMonth() === now.getMonth();

  const shiftPeriod = (months: number) => {
    const nextDate = addMonths(currentPeriod, months);
    const to = qs.stringify({ ...params, period: nextDate.getTime() }, { arrayFormat: "index" });
    navigate(`${location.pathname}?${to}`, { replace: true });
  };

  return (
    <>
      <Box
        width="100%"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        height="90px"
        pr={3}
        borderBottom={`solid 1px ${theme.palette.primary.light}`}
      >
        <Box flexDirection="column">
          <Text fontSize={28} fontWeight="800" paddingLeft="70px">
            <span style={{ color: theme.palette.primary.main }}>G</span>estion <span style={{ color: theme.palette.primary.main }}>C</span>ofidoc,
          </Text>
          <Text fontSize={16} fontWeight="300" paddingLeft="70px">
            Mes comptes rendus de facturation
          </Text>
        </Box>
      </Box>
      <Box
        width="100%"
        flexDirection="column"
        height="calc(100% - 90px)"
        px={3}
        style={{ overflowY: "scroll", overflowX: "hidden" }}
        className="hiddenScrollBarAndChildren"
      >
        <Box width="100%" py={2}>
          <PraticienSelector variant="full" showLabel={false} />
        </Box>
        <Box width="100%" justifyContent="center" alignItems="center" pb={3} style={{ position: "relative" }}>
          <ChevronLeftIcon onClick={() => shiftPeriod(-1)} style={{ cursor: "pointer", color: theme.palette.primary.main, fontSize: 32 }} />
          <Box
            onClick={() => setPeriodVisible((v) => !v)}
            flexDirection="row"
            alignItems="center"
            style={{ cursor: "pointer", margin: "0 12px" }}
          >
            <Text fontSize={18} fontWeight="700" color={theme.palette.primary.main} style={{ textTransform: "capitalize" }}>
              {periodLabel}
            </Text>
            <ArrowDropDownIcon style={{ color: theme.palette.primary.main }} />
          </Box>
          <ChevronRightIcon
            onClick={() => !isCurrentMonth && shiftPeriod(1)}
            style={{
              cursor: isCurrentMonth ? "default" : "pointer",
              color: isCurrentMonth ? theme.palette.grey[300] : theme.palette.primary.main,
              fontSize: 32,
            }}
          />

          {periodVisible && (
            <Box
              style={{
                position: "absolute",
                top: "100%",
                left: "50%",
                transform: "translateX(-50%)",
                marginTop: "8px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                backgroundColor: "#fff",
                boxShadow: "0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1)",
                borderRadius: "5px",
                overflow: "hidden",
                zIndex: 99999,
              }}
            >
              <Calendar
                maxDetail="year"
                onChange={(c) => {
                  const p = new Date(c as any as string);
                  const to = qs.stringify({ ...params, period: p.getTime() }, { arrayFormat: "index" });
                  navigate(`${location.pathname}?${to}`, { replace: true });
                  setPeriodVisible(false);
                }}
                maxDate={new Date()}
                value={currentPeriod}
              />
              <Box p={1}>
                <Button onClick={() => setPeriodVisible(false)}>Fermer</Button>
              </Box>
            </Box>
          )}
        </Box>

        {hasDossiers ? (
          <>
            {!!report.commentaire && (
              <Box flexDirection="column" width="90%" pb={3}>
                <Box height="1px" width={"100%"} bgcolor="#d5d5d5" />
                <Box flexDirection="column" py={2} width="100%">
                  <Text fontSize={13}>Commentaire gestionnaire:</Text>
                  <Text fontSize={17}>{report.commentaire}</Text>
                </Box>
                <Box height="1px" width={"100%"} bgcolor="#d5d5d5" />
              </Box>
            )}

            <Box flexDirection="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box flexDirection="row" alignItems="center">
                <Text fontSize={11} color={theme.palette.grey[600]} style={{ marginRight: 8 }}>
                  Grouper par:
                </Text>
                <FormControl size="small">
                  <Select
                    value={groupMode}
                    onChange={(e) => setGroupMode(e.target.value as FactureGroupMode)}
                    sx={{
                      fontSize: "12px",
                      height: "28px",
                      minWidth: 130,
                      backgroundColor: "white",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.grey[300] },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.main },
                    }}
                  >
                    <MenuItem value="none">
                      <Text fontSize={12}>Aucun</Text>
                    </MenuItem>
                    <MenuItem value="caisse">
                      <Text fontSize={12}>Par caisse</Text>
                    </MenuItem>
                    <MenuItem value="mutuelle">
                      <Text fontSize={12}>Par mutuelle</Text>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Text fontSize={16} fontWeight="700" color={theme.palette.primary.main}>
                {totalMontant.toFixed(2)} €
              </Text>
            </Box>

            {report.dossiers?.map((dossier, dossierIndex) => (
              <DossierSectionMobile key={dossier.dossiers_facturation_id || dossierIndex} dossier={dossier} groupMode={groupMode} />
            ))}
          </>
        ) : (
          <Box justifyContent="center" alignItems="center" width="100%" p={2}>
            <Text fontSize={18} fontWeight={500}>
              Aucun compte rendu pour cette période
            </Text>
          </Box>
        )}
      </Box>
    </>
  );
}
